import { useSuspenseQuery as useTanstackSuspenseQuery, useSuspenseQueries } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Check } from 'lucide-react'
import { z } from 'zod'
import HITLMessage from '~/assets/hitl-message.svg?react'
import { AsyncButton, EmptyState, Icon } from '~/elements'
import { ConversationDetails, ConversationList } from '~/features/hitl/components'
import { SidebarLayout } from '~/layouts'
import { getIntegrationByNameQueryOptions } from '~/queries'
import { FC, useEffect } from 'react'
import { useFeature, useUpdateBot } from '~/hooks'
import { Button, showSuccessToast } from '@bpinternal/ui-kit-next'
import { match } from 'ts-pattern'
import { Boundary, Page } from '~/components'
import { HITLTabs } from '~/features/hitl/components/HITLTabs'
import { useSuspenseQuery, getQueryOptions } from '~/services'
import { cn } from '~/utils'
import { Conversation } from '@botpress/hitl-client'

const paramsSchema = z.object({
  conversationId: z
    .string()
    .optional()
    .catch(() => undefined),
})

export const Route = createFileRoute('/workspaces/$workspaceId/bots/$botId/hitl')({
  component: Component,
  validateSearch: paramsSchema,
})

function Component() {
  const { botId, workspaceId } = Route.useParams()

  const [{ data: workspace }, { data: bot }] = useSuspenseQueries({
    queries: [
      getQueryOptions('workspaces_/$workspaceId_', { workspaceId }),
      getQueryOptions('workspaces_/$workspaceId_/bots_/$botId_', { workspaceId, botId }),
    ],
  })

  const { plan } = workspace
  const isWhiteListed = useFeature('hitlWhitelist')

  const integrationsByName = Object.entries(bot.integrations).map(([intId, int]) => ({ ...int, id: intId }))
  const hitlIntegration = integrationsByName.find((int) => int.name === 'hitl')
  const integrationInstalled = !!(hitlIntegration && hitlIntegration.enabled)

  return (
    <Page title="Human Handoff" className="@container">
      {match({
        plan,
        isWhiteListed,
        integrationInstalled,
      })
        // Case: Community plan + not whitelisted
        .with({ plan: 'community', isWhiteListed: false }, () => <UpgradePlan />)
        // Case: Community/Team/Ent plan + HITL not installed
        .with({ integrationInstalled: false }, () => <InstallHITL />)
        // Case: Community/Team/Ent plan + HITL installed
        .with({ integrationInstalled: true }, () => <HITLDashboard />)
        .exhaustive()}
    </Page>
  )
}

const UpgradePlan = () => {
  const { workspaceId } = Route.useParams()

  return (
    <div className="flex h-[55vh] w-full items-center justify-center">
      <div className={'flex items-center rounded-md border border-gray-5 p-16'} style={{ width: '877px' }}>
        <div className={'flex w-1/2 flex-col gap-6'}>
          <div className="text-2xl font-medium">Take over conversations from the dashboard.</div>
          <div>
            {[
              'Identify escalation criteria',
              'Escalate conversations to human agents',
              'Interact directly with end users',
              'Assign conversations to specific agent',
              'Filter conversations by channel or integration',
              'Pass conversations back to AI agents',
            ].map((feature) => (
              <div key={feature} className="flex items-center gap-2">
                <Icon icon={Check} color="green" />
                <span className="text-gray-11">{feature}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-3">
            <Link to="/workspaces/$workspaceId/billing/plans" params={{ workspaceId: workspaceId }}>
              <Button className="bg-green-9 hover:bg-green-10">Upgrade Plan</Button>
            </Link>
          </div>
        </div>
        <HITLMessage className={'w-1/2'} />
      </div>
    </div>
  )
}

const InstallHITL: FC = () => {
  const { botId, workspaceId } = Route.useParams()
  const { isPending: isBotPending, mutate: updateBot } = useUpdateBot()

  const { data: hitlIntegration } = useTanstackSuspenseQuery(
    getIntegrationByNameQueryOptions({ name: 'hitl', workspaceId })
  )

  const installHITLIntegration = async () => {
    updateBot({
      id: botId,
      workspaceId,
      integrations: {
        [hitlIntegration.id]: { enabled: true, configuration: {} },
      },
      onSuccess: async () => {
        showSuccessToast('HITL integration installed.')
      },
    })
  }

  return (
    <div className="flex h-[55vh] w-full items-center justify-center">
      <EmptyState
        className="m-auto"
        title="Missing HITL Integration"
        description={
          <span>
            <strong>Human Handoff</strong> requires an integration to be installed and enabled.
          </span>
        }
        primaryAction={
          <AsyncButton loading={isBotPending} onClick={installHITLIntegration}>
            <span>Install & enable HITL</span>
          </AsyncButton>
        }
      />
    </div>
  )
}

const HITLDashboard: FC = () => {
  const { botId, workspaceId } = Route.useParams()
  const { conversationId } = Route.useSearch()

  const { data: fetchedConversations, refetch } = useSuspenseQuery(
    `workspaces_/$workspaceId_/bots_/$botId_/conversations/$integrationName/$tags/$participantIds/all`,
    {
      workspaceId,
      botId,
      integrationName: 'hitl',
    }
  )

  useEffect(() => {
    const i = setInterval(refetch, 5000) // yes, long polling..
    return () => clearInterval(i)
  }, [])

  const allConversations: Conversation[] =
    fetchedConversations.map((conversation) => ({
      ...conversation,
      status: conversation.tags['hitl:status'] as Conversation['status'],
      assignee: conversation.tags['hitl:assigneeUserId']
        ? {
            id: conversation.tags['hitl:assigneeUserId'],
            workspaceMemberId: conversation.tags['hitl:assigneeWorkspaceMemberId'],
          }
        : undefined,
      requester: conversation.tags['hitl:requesterUserId']
        ? {
            id: conversation.tags['hitl:requesterUserId'],
          }
        : undefined,
      assignedAt: conversation.tags['hitl:assignedAt'] ?? '',
    })) ?? []

  const currentConversation = conversationId ? allConversations.find((conv) => conv.id === conversationId) : undefined

  return (
    <SidebarLayout
      main={
        <Boundary loader={null}>
          {allConversations.length === 0 ? (
            <div className="flex h-[55vh] w-full items-center justify-center">
              <EmptyState
                className="m-auto"
                title="No Conversations"
                description="No conversations have been escalated yet, your bot is doing a great job!"
              />
            </div>
          ) : currentConversation ? (
            <HITLTabs botId={botId} workspaceId={workspaceId} conversation={currentConversation} />
          ) : (
            <div className="flex h-[55vh] w-full items-center justify-center">
              <EmptyState
                className="m-auto"
                title="No conversation selected"
                description="Select a conversation from the list to start helping your users."
              />
            </div>
          )}
        </Boundary>
      }
      leftSidebarSize="3"
      leftSidebar={
        <>
          {allConversations.length > 0 && (
            <ConversationList
              allConversationsProps={allConversations}
              botId={botId}
              workspaceId={workspaceId}
              currentConversationId={conversationId}
              className={cn({
                'h-[calc(75vh-18rem)] @6xl:h-[calc(100vh-18rem)]': !!currentConversation,
                'h-[calc(100vh-18rem)]': !currentConversation,
              })}
            />
          )}
          {currentConversation && (
            <div className="mb-4 @2xl:hidden">
              <ConversationDetails botId={botId} workspaceId={workspaceId} currentConversation={currentConversation} />
            </div>
          )}
        </>
      }
      rightSidebar={
        currentConversation && (
          <ConversationDetails botId={botId} workspaceId={workspaceId} currentConversation={currentConversation} />
        )
      }
      rightSidebarClassName="hidden @2xl:flex"
    />
  )
}
